if (!customElements.get('hub-notification-feed')) {
  customElements.define('hub-notification-feed', class extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.hub = this.closest('simpson-customer-hub');
      this.preview = this.dataset.preview === 'true';
      this.customerId = this.dataset.customerId || '';
      this.key = this.customerId && !this.preview ? `simpson:hub:notifications:v1:${this.dataset.themeId}:${this.customerId}` : null;
      this.read = new Set();
      this.signedOut = false;
      this.storageAvailable = true;
      this.items = Array.from(this.querySelectorAll('[data-notification-id]'), element => ({
        element,
        fingerprint: JSON.stringify([
          element.dataset.notificationId, element.dataset.notificationRevision,
          element.querySelector('[data-notification-title]')?.textContent.trim(),
          element.querySelector('[data-notification-body]')?.textContent.trim(),
          element.querySelector('[data-notification-action]')?.getAttribute('href'),
          element.querySelector('[data-notification-action]')?.dataset.hubGo,
          element.dataset.notificationStart, element.dataset.notificationEnd
        ]),
        start: window.SimpsonHubSchedule?.timestamp(element.dataset.notificationStart) ?? (element.dataset.notificationStart ? NaN : null),
        end: window.SimpsonHubSchedule?.timestamp(element.dataset.notificationEnd) ?? (element.dataset.notificationEnd ? NaN : null)
      }));
      this.loadRead();
      const options = { signal: this.controller.signal };
      this.addEventListener('click', event => {
        if (event.target.closest('[data-notifications-read-all]')) {
          this.items.filter(item => !item.element.hidden).forEach(item => this.read.add(item.fingerprint));
          this.saveRead();
          this.update();
          this.querySelector('[data-notification-status]').textContent = this.dataset.allRead;
          this.closest('[data-hub-view]')?.querySelector('h3')?.focus();
          return;
        }
        const action = event.target.closest('[data-notification-mark], [data-notification-action]');
        if (!action) return;
        const item = this.items.find(item => item.element.contains(action));
        if (!item || item.element.hidden) return;
        this.read.add(item.fingerprint);
        this.saveRead();
        this.update();
        if (action.matches('[data-notification-mark]')) {
          const heading = item.element.querySelector('h4');
          heading.tabIndex = -1;
          heading.focus();
        }
      }, options);
      document.addEventListener('simpson:notifications:request', () => this.update(), options);
      document.addEventListener('visibilitychange', () => this.update(), options);
      document.addEventListener('click', event => {
        if (event.target.closest('[data-hub-account-sign-out]')) {
          this.signedOut = true;
          this.read.clear();
          this.update();
        }
      }, options);
      window.addEventListener('storage', event => {
        if (!this.key || (event.key !== this.key && event.key !== null)) return;
        this.loadRead();
        this.update();
      }, options);
      window.addEventListener('focus', () => this.update(), options);
      window.addEventListener('pageshow', () => this.update(), options);
      this.hub?.querySelector('simpson-hub-drawer')?.addEventListener('toggle', () => this.update(), options);
      this.update();
      if (this.dataset.connectedSources === 'true' && !this.preview) {
        customElements.whenDefined('simpson-customer-hub').then(() => {
          if (!this.controller.signal.aborted) this.sources = new window.SimpsonHubNotificationSources(this);
        });
      }
    }

    loadRead() {
      if (!this.key || !this.storageAvailable) return;
      try {
        const stored = JSON.parse(localStorage.getItem(this.key) || '[]');
        this.read = new Set(Array.isArray(stored) ? stored.filter(value => typeof value === 'string').slice(-500) : []);
      } catch { this.storageAvailable = false; }
    }

    saveRead() {
      if (!this.key || !this.storageAvailable) return;
      try {
        const current = new Set(this.items.map(item => item.fingerprint));
        const stored = new Set(JSON.parse(localStorage.getItem(this.key) || '[]'));
        this.read.forEach(value => stored.add(value));
        this.read = new Set(Array.from(stored).filter(value => current.has(value)).slice(-500));
        localStorage.setItem(this.key, JSON.stringify(Array.from(this.read)));
      } catch { this.storageAvailable = false; }
    }

    update() {
      clearTimeout(this.timer);
      if (this.controller.signal.aborted) return;
      const now = Date.now();
      let next = Infinity;
      let count = 0;
      let visible = 0;
      for (const item of this.items) {
        const invalid = Number.isNaN(item.start) || Number.isNaN(item.end) || (item.start !== null && item.end !== null && item.end <= item.start);
        const active = (!item.private || !!this.sources?.valid()) && !item.suspended && !this.signedOut && (this.customerId || this.preview) && !invalid && (item.start === null || now >= item.start) && (item.end === null || now < item.end);
        item.element.hidden = !active;
        if (!invalid) for (const boundary of [item.start, item.end]) if (boundary !== null && boundary > now) next = Math.min(next, boundary);
        const read = this.read.has(item.fingerprint);
        item.element.dataset.read = String(read);
        item.element.querySelector('[data-notification-unread]').hidden = read;
        item.element.querySelector('[data-notification-read]').hidden = !read;
        item.element.querySelector('[data-notification-mark]').hidden = read;
        if (active) {
          visible++;
          if (!read) count++;
        }
      }
      const empty = this.querySelector('[data-notification-empty]');
      if (empty) empty.hidden = visible > 0;
      const markAll = this.querySelector('[data-notifications-read-all]');
      if (markAll) markAll.disabled = count === 0;
      const storageNote = this.querySelector('[data-notification-storage]');
      if (storageNote && !this.storageAvailable) storageNote.textContent = storageNote.dataset.fallback;
      if (storageNote && this.preview) storageNote.hidden = true;
      this.hub?.querySelectorAll('[data-hub-notifications-count]').forEach(badge => {
        badge.hidden = count === 0;
        badge.textContent = count > 9 ? '9+' : String(count);
      });
      document.dispatchEvent(new CustomEvent('simpson:notifications:updated', { detail: { customerId: this.customerId, unreadCount: count, preview: this.preview } }));
      if (next !== Infinity) this.timer = setTimeout(() => this.update(), Math.max(1, Math.min(60000, next - now)));
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.sources?.destroy();
      this.controller?.abort();
      document.dispatchEvent(new CustomEvent('simpson:notifications:updated', { detail: { customerId: this.customerId, unreadCount: 0, preview: this.preview } }));
    }
  });
}


